import { PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { useQueryParam } from 'hooks/useQueryParam';
import { useMemo, useState, useEffect } from 'react';

import { ActionsWrapper } from 'ui/ActionsWrapper';
import { DiagnosticService } from 'services/DiagnosticService';
import { usePagination } from 'hooks/usePagination';
import { DiagnosticForm } from './DiagnosticForm';
import { DiagnosticDetail } from './DianosticDetail';
import DiagnosticTable from './DiagnosticTable';
import { DiagnosticResult } from './DiagnosticResult';

const PAGE_TITLE = 'Диагностика';

function Diagnostic() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  useHeaderChanger(PAGE_TITLE);
  const [clients, setClients] = useState(null);
  const [diagnostic, setDiagnostic] = useState([]);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
  });
  const [loadingTable, setLoadingTable] = useState(true);
  const { client_id: selectedClient } = filters;
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination({ updateQuery: false });

  useEffect(() => {
    getDiagnostic();
  }, [page, perPage]);

  useEffect(() => {
    return () => resetPagination();
  }, []);

  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    closeForm,
  } = useDrawers();
  const rowDrawer = useDrawers();

  const selectedClientInfo = useMemo(() => {
    if (clients?.length > 0) {
      const client = clients.find(
        (item) => item?.id === Number(selectedClient)
      );

      if (client) {
        return {
          full_name: client?.full_name,
          iin: client?.bin_iin,
          leader: client?.leader,
          address: client?.address_legal,
          nds: client?.nds,
        };
      }
    }

    return null;
  }, [selectedClient, clients]);

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
  };

  const getDiagnostic = async () => {
    try {
      setLoadingTable(true);
      const res = await DiagnosticService.getDiagnostic(page, perPage);
      if (res) {
        setDiagnostic(res);
        setTotalRows(res.total);
        setLoadingTable(false);
      }
    } catch (error) {
      message.error('Не удалось получить диагностики');
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    getDiagnostic();
  }, []);

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
          onClientsChange={setClients}
        />

        <AccessControl pageId="diagnostic" action="create">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              openCreateForm(selectedClientInfo);
            }}
          >
            Получить Диагностику
          </Button>
        </AccessControl>
      </ActionsWrapper>

      <DiagnosticForm
        visible={formIsActive}
        clientId={clientId}
        closeDrawer={closeForm}
        openDrawer={openDrawer}
      />

      {activeRecord && (
        <DiagnosticDetail
          visible={drawerVisible}
          closeDrawer={closeDrawer}
          activeRecord={activeRecord}
          closeForm={closeForm}
          getDiagnostic={getDiagnostic}
        />
      )}

      <DiagnosticTable
        items={diagnostic}
        onRowClick={rowDrawer.openDrawer}
        activeRecord={rowDrawer.state.activeRecord}
        getDiagnostic={getDiagnostic}
        perPage={perPage}
        setPagination={setPagination}
        totalRows={totalRows}
        loading={loadingTable}
      />

      <DiagnosticResult
        visible={rowDrawer.state.drawerVisible}
        drawerClose={rowDrawer.closeDrawer}
        activeRecord={rowDrawer.state.activeRecord}
      />
    </>
  );
}

export { Diagnostic };
